import React, { useCallback, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import PageHeader from "../../partials/pageHeader";
import CompanyappLoader from "../../partials/companyappLoader";
import NoData from "../../partials/noData";
import Pagination from "../../partials/pagination";
import AlertModal from "../../partials/alertModal";
import { getOnlineNode } from "../../core/getNode";
import { postNode, patchNode } from "../../core/postNode";
import { deleteRequest } from "../../core/delete";

const TAGS_BASE = "api/companyapp/tags";
const PAGE_SIZE = 24;

export default function CompanyappTags({
  asModal = false,
  open = true,
  onClose,
}) {
  const user = useSelector((state) => state).authReducer.user;

  const [tags, setTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [draftName, setDraftName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const [feedback, setFeedback] = useState(null); // { severity, message }
  const [pendingDelete, setPendingDelete] = useState(null);

  const refresh = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const { data } = await getOnlineNode(
        `${TAGS_BASE}?_format=json&page=${page}&items_per_page=${PAGE_SIZE}`,
      );
      setTags(Array.isArray(data?.rows) ? data.rows : []);
      setTotalItems(data?.pager?.total_items ?? 0);
      setTotalPages(data?.pager?.total_pages ?? 0);
    } catch (err) {
      setFeedback({
        severity: "error",
        message: extractError(err, "Failed to load tags."),
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    if (user?.current_user) refresh(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.current_user, currentPage]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = draftName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await postNode(
        TAGS_BASE,
        { name, description: "" },
        user.csrf_token,
      );
      setDraftName("");
      setFeedback({ severity: "success", message: `Created "${data.name}".` });
      await refresh(currentPage);
    } catch (err) {
      setFeedback({
        severity: "error",
        message: extractError(err, "Failed to create tag."),
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditingValue(tag.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleRename = async (tag) => {
    const next = editingValue.trim();
    if (!next || next === tag.name) {
      cancelEdit();
      return;
    }
    setBusyId(tag.id);
    try {
      await patchNode(
        `${TAGS_BASE}/${tag.id}`,
        { name: next },
        user.csrf_token,
      );
      setFeedback({ severity: "success", message: "Tag renamed." });
      cancelEdit();
      await refresh(currentPage);
    } catch (err) {
      setFeedback({
        severity: "error",
        message: extractError(err, "Failed to rename tag."),
      });
    } finally {
      setBusyId(null);
    }
  };

  const requestDelete = (tag) => {
    setPendingDelete(tag);
  };

  const confirmDelete = async () => {
    const tag = pendingDelete;
    if (!tag) return;
    setBusyId(tag.id);
    try {
      await deleteRequest(`${TAGS_BASE}/${tag.id}?_format=json`);
      setFeedback({ severity: "success", message: `Deleted "${tag.name}".` });
      // If the current page is empty after delete, step back one page.
      const remaining = totalItems - 1;
      const lastPage = Math.max(0, Math.ceil(remaining / PAGE_SIZE) - 1);
      const nextPage = Math.min(currentPage, lastPage);
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await refresh(currentPage);
      }
    } catch (err) {
      setFeedback({
        severity: "error",
        message: extractError(err, "Failed to delete tag."),
      });
    } finally {
      setBusyId(null);
      setPendingDelete(null);
    }
  };

  const renderPagination = () => {
    if (totalItems <= PAGE_SIZE) return null;
    return (
      <Pagination
        owner="admin"
        containerClasses="creator-pagination d-flex justify-content-center align-items-center mt-4"
        previousDisabled={currentPage === 0}
        nextDisabled={currentPage + 1 >= totalPages}
        currentPage={currentPage + 1}
        onPrevious={() => setCurrentPage((p) => Math.max(p - 1, 0))}
        onNext={() => setCurrentPage((p) => p + 1)}
      />
    );
  };

  const isInitialLoad = loading && tags.length === 0;

  const tagsBody = (
    <>
      {!asModal && <PageHeader pageName="My Tags" filters={false} />}

      <Typography variant="body2" sx={{ mb: 2, opacity: 0.75 }}>
        Tags you create here are private to your account and can be applied to
        any item you publish.
      </Typography>

      {isInitialLoad ? (
        <CompanyappLoader />
      ) : (
        <>
          <form onSubmit={handleCreate} className="mb-4">
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                placeholder="New tag name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                disabled={creating}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  creating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
                disabled={creating || !draftName.trim()}
              >
                Add
              </Button>
            </Stack>
          </form>

          {tags.length === 0 ? (
            <NoData message="No tags yet — create your first one above." />
          ) : (
            <Stack spacing={1} sx={{ opacity: loading ? 0.5 : 1 }}>
              {tags.map((tag) => {
                const isEditing = editingId === tag.id;
                const isBusy = busyId === tag.id;

                return (
                  <Row
                    key={tag.id}
                    className="align-items-center px-3 py-2 tag-row"
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 8,
                      margin: 0,
                      marginBottom: 8,
                    }}
                  >
                    <Col className="ps-0">
                      {isEditing ? (
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(tag);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          disabled={isBusy}
                        />
                      ) : (
                        <Typography variant="body1">{tag.name}</Typography>
                      )}
                    </Col>
                    <Col xs="auto" className="pe-0">
                      {isEditing ? (
                        <>
                          <Tooltip title="Save">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRename(tag)}
                                disabled={isBusy}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <span>
                              <IconButton
                                size="small"
                                onClick={cancelEdit}
                                disabled={isBusy}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Rename">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => startEdit(tag)}
                                disabled={isBusy}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => requestDelete(tag)}
                                disabled={isBusy}
                              >
                                {isBusy ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Col>
                  </Row>
                );
              })}
            </Stack>
          )}

          {renderPagination()}
        </>
      )}
    </>
  );

  return (
    <>
      {asModal ? (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="sm"
          scroll="paper"
          className="companyapp-tags-dialog"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
            }}
          >
            <span>My Tags</span>
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent dividers>{tagsBody}</DialogContent>
        </Dialog>
      ) : (
        <main className="companyapp-tags screen d-flex flex-column h-100">
          <Row>
            <Col xs={12} lg={6} className="mx-lg-auto">
              {tagsBody}
            </Col>
          </Row>
        </main>
      )}

      <AlertModal
        showAlert={Boolean(pendingDelete)}
        showAlertCallback={(_open) => {
          if (!_open) setPendingDelete(null);
        }}
        warningIcon
        warningIconColor="error"
        alertMessageTitle="Delete tag?"
        alertMessageBody={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed from any items it's applied to. This cannot be undone.`
            : ""
        }
        cancelButton
        cancelButtonLabel="Cancel"
        confirmButton
        confirmButtonLabel="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

function extractError(err, fallback) {
  return (
    err?.response?.data?.message ||
    err?.response?.data ||
    err?.message ||
    fallback
  );
}
